import { TemplateGallery, TemplatePickerModal } from '../components/dashboard/myData/TemplateGallery';
import { buildDashboardFromTemplate, tradeWatcherSection, shiftSizes } from '../components/dashboard/myData/myDashboardTemplates';
import { loadTwAccounts } from '../components/dashboard/myData/tradeWatcherData';
import { isTradeWatcherWidgetType } from '../components/dashboard/myData/myWidgetRegistry';
import { loadMyAccounts, describeAccountChoice } from '../components/dashboard/myData/myDataApi';
import { MY_KPI_OPTIONS } from '../components/dashboard/myData/MyDataWidgets';
import WelcomeOverview from '../components/welcome/WelcomeOverview';
import { useWorkspace } from '../utils/workspace';
import BrandGettingStarted from '../components/BrandGettingStarted';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './DashboardPage.css';

import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getEntityDisplayName, getResultsNavLabel } from '../utils/activeEntity';
import PageHeading from '../components/PageHeading';
import {
  IconChart,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconGrip,
  IconLock,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconX,
} from '../components/Icons';
import { membershipDeniedFallbackMessage } from '../utils/userFacingStrategyErrors';
import DashboardToolbar from '../components/dashboard/DashboardToolbar';
import {
  MAX_DASHBOARDS,
  apiListDashboards as apiList,
  apiGetDashboard as apiGet,
  apiCreateDashboard as apiCreate,
  apiUpdateDashboard as apiUpdate,
  apiDeleteDashboard as apiDelete,
  apiGetDefaultTemplate,
  apiPutDefaultTemplate,
} from '../components/dashboard/dashboardApi';
import WidgetErrorBoundary from '../components/dashboard/WidgetErrorBoundary';
import {
  getDashboardWidgetMeta,
  dashboardHasVcWidgets,
  widgetTypeGroupsForDashboard,
  isVcWidgetType,
  isMyWidgetType,
} from '../components/dashboard/dashboardWidgetRegistry';
import VectorCapitalDashboardHost from '../components/vectorCapital/VectorCapitalDashboardHost';
import { regularSheetNamesOnly } from '../components/dashboard/resolveStrategyDisplayName';
import { KPI_METRIC_OPTIONS } from '../components/dashboard/KpiCardWidget';
import {
  readActiveDashboardId,
  readCachedDashboard,
  readCachedDashboardList,
  writeActiveDashboardId,
  writeCachedDashboard,
  writeCachedDashboardList,
  clearCachedDashboard,
  hasMigratedToVectorDefault,
} from '../components/dashboard/dashboardCache';
import { migratePersonalDefaultsToVector } from '../components/dashboard/vectorDefaultMigration';
import {
  VECTOR_DASHBOARD_ID,
  orgDashboardName,
  buildVectorDashboard,
  isVectorDashboardId,
} from '../components/dashboard/vectorDashboardTemplate';

const SAVE_DEBOUNCE_MS = 500;
// Max saved custom strat combos per user. Must match MAX_COMBOS_PER_USER in
// server/routes/customMultistrats.js.
const MAX_CUSTOM_COMBOS = 5;
/** Pixel height of one grid row track. Widgets are sized in row units; pixel height = h * ROW_PX (+ gaps). */
const ROW_PX = 80;
/** Gap between grid cells in pixels — must match `gap` in `.dashboard-grid` CSS. */
const GRID_GAP_PX = 16;
/** Desktop dashboard columns. Each widget has explicit {col, row, w, h} placement. */
const GRID_TRACKS = 12;
/** Upper bound on row span — keeps clamp logic finite without limiting realistic widgets. */
const MAX_ROW_SPAN = 24;

/** Wide widgets always stack full-width on mobile (never squeeze side-by-side). */
const MOBILE_FULL_WIDTH_WIDGET_TYPES = new Set([
  'calendar',
  'weeklyChart',
  'currentResultsTable',
]);

/**
 * Only single-number cards may sit two side by side on a phone. Everything else
 * (lists, tables, news, calendars, alerts) gets the whole width: at half a phone
 * screen their text was cut off and the other half was left empty.
 */
const MOBILE_HALF_WIDTH_WIDGET_TYPES = new Set(['kpi', 'myKpi', 'mkSymbol']);

/**
 * Minimum row span on a phone (mobile rows are shorter, and these widgets stack
 * their contents there): enough to show everything without cutting it off.
 */
const MOBILE_MIN_ROWS = {
  twToday: 6,
  twAlerts: 7,
  twAccounts: 7,
  twDailyPnl: 7,
  twCalendar: 7,
  myTrades: 6,
  myPositions: 6,
  mkNews: 7,
  mkCalendar: 7,
};

function getWidgetMeta(type) {
  return getDashboardWidgetMeta(type);
}

function clampInt(n, lo, hi, fallback) {
  const x = Math.round(Number(n));
  if (!Number.isFinite(x)) return fallback;
  return Math.min(hi, Math.max(lo, x));
}

function minWForType(type) {
  const m = getWidgetMeta(type);
  const raw = m?.minSize?.w ?? m?.minGridSpan;
  return clampInt(raw, 1, GRID_TRACKS, 1);
}

function minHForType(type) {
  const raw = getWidgetMeta(type)?.minSize?.h;
  return clampInt(raw, 1, MAX_ROW_SPAN, 1);
}

function defaultWForType(type) {
  const m = getWidgetMeta(type);
  const raw = m?.defaultSize?.w ?? m?.defaultGridSpan;
  return clampInt(raw, minWForType(type), GRID_TRACKS, 6);
}

function defaultHForType(type) {
  const m = getWidgetMeta(type);
  const raw = m?.defaultSize?.h ?? m?.gridRowSpan;
  return clampInt(raw, minHForType(type), MAX_ROW_SPAN, 4);
}

function rectsOverlap(a, b) {
  return (
    a.col < b.col + b.w
    && b.col < a.col + a.w
    && a.row < b.row + b.h
    && b.row < a.row + a.h
  );
}

function placementCollides(p, others) {
  return others.some((o) => rectsOverlap(p, o));
}

function clampPlacement(p, type) {
  const minW = minWForType(type);
  const minH = minHForType(type);
  const w = clampInt(p?.w, minW, GRID_TRACKS, defaultWForType(type));
  const h = clampInt(p?.h, minH, MAX_ROW_SPAN, defaultHForType(type));
  const col = clampInt(p?.col, 1, GRID_TRACKS - w + 1, 1);
  const row = clampInt(p?.row, 1, 500, 1);
  return { col, row, w, h };
}

/**
 * Find the first row-major (col, row) where a {w, h}-sized widget fits without
 * overlapping any existing placement. Used when adding a new widget or unhiding one.
 */
function findFirstFreeSlot(w, h, placedRects) {
  const maxOccupiedRow = placedRects.reduce((m, r) => Math.max(m, r.row + r.h - 1), 0);
  for (let row = 1; row <= maxOccupiedRow + 1; row += 1) {
    for (let col = 1; col + w - 1 <= GRID_TRACKS; col += 1) {
      const candidate = { col, row, w, h };
      if (!placementCollides(candidate, placedRects)) return candidate;
    }
  }
  return { col: 1, row: maxOccupiedRow + 1, w, h };
}

/**
 * Resolve a drop: place `draggedId` at `target` and reconcile collisions.
 *   - If the target overlaps exactly one widget AND both widgets share the same w×h,
 *     swap them — the displaced widget moves to the dragged widget's old slot.
 *   - Otherwise, push every overlapping widget to its first free slot (row-major) in
 *     the layout that results from the dragged widget being placed at `target`.
 * Returns a new sizes map (does not mutate the input).
 */
function resolveDrop(sizes, draggedId, oldPlacement, target) {
  const overlapping = Object.entries(sizes)
    .filter(([id, p]) => id !== draggedId && p && rectsOverlap(p, target));

  if (overlapping.length === 0) {
    return { ...sizes, [draggedId]: target };
  }

  // Same-size single-overlap → swap into the dragged widget's old slot if it fits.
  if (overlapping.length === 1 && oldPlacement) {
    const [otherId, otherP] = overlapping[0];
    if (otherP.w === oldPlacement.w && otherP.h === oldPlacement.h) {
      const swapTarget = { ...otherP, col: oldPlacement.col, row: oldPlacement.row };
      const others = Object.entries(sizes)
        .filter(([id]) => id !== draggedId && id !== otherId)
        .map(([, p]) => p);
      if (!placementCollides(swapTarget, others)) {
        return { ...sizes, [draggedId]: target, [otherId]: swapTarget };
      }
    }
  }

  // General displace → push each overlapping widget to the first free slot in the
  // post-drop layout. We seed `placed` with non-overlapping widgets plus the dragged
  // widget at its new target, then relocate the overlapping ones one at a time.
  const result = {};
  Object.entries(sizes).forEach(([id, p]) => {
    if (id === draggedId) return;
    if (overlapping.some(([oid]) => oid === id)) return;
    result[id] = p;
  });
  result[draggedId] = target;
  overlapping.forEach(([id, p]) => {
    const placed = Object.values(result);
    result[id] = findFirstFreeSlot(p.w, p.h, placed);
  });
  return result;
}

/** Pack `[{id, w, h}]` into the 12-track grid in row-major order (matches legacy auto-flow). */
function packLegacyOrder(items) {
  let curRow = 1;
  let curCol = 1;
  let rowMaxH = 0;
  const out = {};
  items.forEach(({ id, w, h }) => {
    if (curCol + w - 1 > GRID_TRACKS) {
      curRow += rowMaxH || 1;
      curCol = 1;
      rowMaxH = 0;
    }
    out[id] = { col: curCol, row: curRow, w, h };
    curCol += w;
    rowMaxH = Math.max(rowMaxH, h);
  });
  return out;
}

function generateClientId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `w-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

/**
 * Build a Vector Dashboard synthetic, preferring server overrides when a
 * VectorAdmin/Admin has published a customized layout (published_at non-null
 * and layout has widgets). Returns code defaults otherwise.
 */
function templateHasPublishedContent(template) {
  if (!template) return false;
  const order = template.layout?.order;
  const widgets = template.widgets;
  return Array.isArray(order) && order.length > 0
    && Array.isArray(widgets) && widgets.length > 0;
}

function makeVectorSynthetic(serverTemplate, options = {}) {
  const base = buildVectorDashboard(options);
  if (!serverTemplate?.published_at || !templateHasPublishedContent(serverTemplate)) {
    return base;
  }
  return {
    ...base,
    layout: serverTemplate.layout,
    widgets: serverTemplate.widgets,
  };
}

/** Persist the resolved Vector dashboard so the next mount can render hidden widgets immediately. */
function rememberVectorDashboard(email, dashboard) {
  if (!email || !dashboard) return;
  writeCachedDashboard(email, {
    ...dashboard,
    id: VECTOR_DASHBOARD_ID,
    is_system: true,
  });
}

/**
 * Prefer a published server template when we have one; otherwise use the user's
 * cached Vector dashboard (layout + hidden widgets) before code defaults.
 */
function resolveVectorDashboard(email, serverTemplate, options = {}) {
  const fromServer = makeVectorSynthetic(serverTemplate, options);
  const useServer = serverTemplate?.published_at && templateHasPublishedContent(serverTemplate);
  if (useServer) {
    return normalizeDashboardLayout(fromServer);
  }
  const cached = email ? readCachedDashboard(email, VECTOR_DASHBOARD_ID) : null;
  return normalizeDashboardLayout(cached || fromServer);
}

/** Synchronous cache read for instant first paint (personal or Vector dashboard). */
function hydrateDashboardFromCache(email) {
  if (!email) return null;
  const activeId = readActiveDashboardId(email);
  if (activeId && !isVectorDashboardId(activeId)) {
    const cached = readCachedDashboard(email, activeId);
    return cached ? normalizeDashboardLayout(cached) : null;
  }
  const cachedVector = readCachedDashboard(email, VECTOR_DASHBOARD_ID);
  return cachedVector ? normalizeDashboardLayout(cachedVector) : null;
}


/* ─────────────────────────────────────────────────────────────────────────── */
/*  Layout shape                                                               */
/* ─────────────────────────────────────────────────────────────────────────── */

function emptyLayout() {
  return { order: [], sizes: {}, hidden: [] };
}

/** Read placement for a widget. Falls back to defaults for legacy/missing entries. */
function getWidgetPlacement(layout, id, type) {
  const stored = layout?.sizes?.[id];
  if (stored
      && typeof stored.col === 'number'
      && typeof stored.row === 'number'
      && typeof stored.w === 'number'
      && typeof stored.h === 'number') {
    return clampPlacement(stored, type);
  }
  // Legacy v1 fallback: derive w from gridSpan, h from height/ROW_PX.
  // (normalizeDashboardLayout should have already migrated, but handle defensively.)
  const w = clampInt(stored?.gridSpan, minWForType(type), GRID_TRACKS, defaultWForType(type));
  const h = stored?.height
    ? clampInt(Math.round(stored.height / ROW_PX), minHForType(type), MAX_ROW_SPAN, defaultHForType(type))
    : defaultHForType(type);
  return clampPlacement({ col: 1, row: 1, w, h }, type);
}

/**
 * Normalize a dashboard payload to the v2 placement shape:
 *   layout = { order: string[], sizes: { [id]: {col, row, w, h} }, hidden: string[] }
 *
 * Accepts and migrates two legacy shapes:
 *   - v0: an array of react-grid-layout items `[{i, x, y, w, h, ...}]`
 *   - v1: object form with `sizes[id] = {gridSpan, height}` (auto-flow placement)
 *
 * `appendMissing` controls whether widgets not referenced in the layout should be
 * auto-added to `order`. Enabled on load (so legacy dashboards render); disabled when
 * normalizing a save response (so a server that strips fields can't silently un-hide).
 */
function normalizeDashboardLayout(dashboard, { appendMissing = true } = {}) {
  if (!dashboard) return dashboard;
  const widgets = Array.isArray(dashboard.widgets) ? dashboard.widgets : [];
  const widgetIds = new Set(widgets.map((w) => w.id));
  const widgetById = new Map(widgets.map((w) => [w.id, w]));
  const rawLayout = dashboard.layout;

  // ── v2 / v1 (object form) ──────────────────────────────────────────────────
  if (rawLayout && !Array.isArray(rawLayout) && typeof rawLayout === 'object') {
    const order = Array.isArray(rawLayout.order)
      ? rawLayout.order.filter((id) => widgetIds.has(id))
      : [];
    const hidden = Array.isArray(rawLayout.hidden)
      ? rawLayout.hidden.filter((id) => widgetIds.has(id))
      : [];
    const inSizes = (rawLayout.sizes && typeof rawLayout.sizes === 'object') ? rawLayout.sizes : {};

    if (appendMissing) {
      const known = new Set([...order, ...hidden]);
      widgets.forEach((w) => { if (!known.has(w.id)) order.push(w.id); });
    }

    const sizes = {};
    const needsPacking = []; // {id, w, h} — entries without a v2 placement
    Object.entries(inSizes).forEach(([id, entry]) => {
      if (!widgetIds.has(id) || !entry || typeof entry !== 'object') return;
      const t = widgetById.get(id)?.type || '';
      const hasV2 = typeof entry.col === 'number'
        && typeof entry.row === 'number'
        && typeof entry.w === 'number'
        && typeof entry.h === 'number';
      if (hasV2) {
        sizes[id] = clampPlacement(entry, t);
      } else {
        const w = clampInt(entry.gridSpan, minWForType(t), GRID_TRACKS, defaultWForType(t));
        const h = entry.height
          ? clampInt(Math.round(entry.height / ROW_PX), minHForType(t), MAX_ROW_SPAN, defaultHForType(t))
          : defaultHForType(t);
        needsPacking.push({ id, w, h });
      }
    });
    // Visible widgets without any sizes entry also need a placement.
    order.forEach((id) => {
      if (!sizes[id] && !needsPacking.find((n) => n.id === id)) {
        const t = widgetById.get(id)?.type || '';
        needsPacking.push({ id, w: defaultWForType(t), h: defaultHForType(t) });
      }
    });

    if (needsPacking.length > 0) {
      // Pack in `order` sequence so visual order matches legacy auto-flow.
      const orderIndex = new Map(order.map((id, i) => [id, i]));
      const ordered = needsPacking
        .filter((n) => orderIndex.has(n.id))
        .sort((a, b) => orderIndex.get(a.id) - orderIndex.get(b.id));
      const orphans = needsPacking.filter((n) => !orderIndex.has(n.id));
      const packed = packLegacyOrder([...ordered, ...orphans]);
      const placedRects = Object.values(sizes).slice();
      Object.entries(packed).forEach(([id, p]) => {
        // If the packed slot collides with already-placed v2 items, fall back to first-free.
        const slot = placedRects.some((e) => rectsOverlap(p, e))
          ? findFirstFreeSlot(p.w, p.h, placedRects)
          : p;
        sizes[id] = slot;
        placedRects.push(slot);
      });
    }

    return { ...dashboard, layout: { order, sizes, hidden } };
  }

  // ── v0 (RGL array) ─────────────────────────────────────────────────────────
  if (Array.isArray(rawLayout)) {
    const items = rawLayout
      .filter((it) => it && widgetIds.has(it.i))
      .slice()
      .sort((a, b) => {
        if ((a.y ?? 0) !== (b.y ?? 0)) return (a.y ?? 0) - (b.y ?? 0);
        return (a.x ?? 0) - (b.x ?? 0);
      });
    const order = [];
    const sizes = {};
    const placedRects = [];
    items.forEach((it) => {
      const t = widgetById.get(it.i)?.type || '';
      const w = clampInt(it.w, minWForType(t), GRID_TRACKS, defaultWForType(t));
      const h = clampInt(it.h, minHForType(t), MAX_ROW_SPAN, defaultHForType(t));
      const colIn = clampInt((it.x ?? 0) + 1, 1, GRID_TRACKS - w + 1, 1);
      const rowIn = clampInt((it.y ?? 0) + 1, 1, 500, 1);
      const candidate = { col: colIn, row: rowIn, w, h };
      const slot = placementCollides(candidate, placedRects)
        ? findFirstFreeSlot(w, h, placedRects)
        : candidate;
      sizes[it.i] = slot;
      placedRects.push(slot);
      order.push(it.i);
    });
    if (appendMissing) {
      widgets.forEach((w) => {
        if (!order.includes(w.id)) {
          const t = w.type;
          const slot = findFirstFreeSlot(defaultWForType(t), defaultHForType(t), placedRects);
          sizes[w.id] = slot;
          placedRects.push(slot);
          order.push(w.id);
        }
      });
    }
    return { ...dashboard, layout: { order, sizes, hidden: [] } };
  }

  // ── No layout at all ───────────────────────────────────────────────────────
  if (appendMissing) {
    const order = widgets.map((w) => w.id);
    const sizes = {};
    const placedRects = [];
    order.forEach((id) => {
      const t = widgetById.get(id)?.type || '';
      const slot = findFirstFreeSlot(defaultWForType(t), defaultHForType(t), placedRects);
      sizes[id] = slot;
      placedRects.push(slot);
    });
    return { ...dashboard, layout: { order, sizes, hidden: [] } };
  }
  return { ...dashboard, layout: { order: [], sizes: {}, hidden: [] } };
}


/* ─────────────────────────────────────────────────────────────────────────── */
/*  Customize panel — sortable active widgets + hidden widgets + add widget    */
/* ─────────────────────────────────────────────────────────────────────────── */

function SortableCustomizeRow({ id, label, subtitle, onHide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dashboard-customize-row ${isDragging ? 'is-dragging' : ''}`}
    >
      <button
        type="button"
        className="dashboard-customize-grip"
        {...attributes}
        {...listeners}
        aria-label={`Drag ${label}`}
      >
        <IconGrip size={16} />
      </button>
      <div className="dashboard-customize-row-text">
        <strong>{label}</strong>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
      <button
        type="button"
        className="dashboard-customize-row-action"
        onClick={onHide}
        aria-label={`Hide ${label}`}
        title="Hide widget"
      >
        <IconEyeOff size={14} />
      </button>
    </div>
  );
}

function CustomizePanel({
  dashboard,
  customCombos,
  onReorder,
  onToggleVisibility,
  onAddWidget,
  onRemove,
  onClose,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const layout = dashboard.layout || emptyLayout();
  const widgetsById = useMemo(() => {
    const m = new Map();
    (dashboard.widgets || []).forEach((w) => m.set(w.id, w));
    return m;
  }, [dashboard.widgets]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder((prev) => {
      const oldIdx = prev.indexOf(active.id);
      const newIdx = prev.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const renderRow = (id) => {
    const widget = widgetsById.get(id);
    if (!widget) return null;
    const meta = getWidgetMeta(widget.type);
    // Build a "type · sub-type · strategy" subtitle so the reorder list can
    // tell apart multiple KPI Cards on the same dashboard (Win Rate vs Best
    // Day vs Week Win Rate, all of which would otherwise read identically).
    const parts = [meta?.label || widget.type];
    const mine = widget.config?.source === 'mine' || isMyWidgetType(widget.type);
    // "My accounts" widgets: which number and which accounts, so rows tell apart.
    if (widget.type === 'myKpi' || (widget.type === 'kpi' && widget.config?.source === 'mine')) {
      const key = widget.type === 'myKpi' ? widget.config?.metric : widget.config?.myMetric;
      const label = MY_KPI_OPTIONS.find((o) => o.value === (key || 'netPnl'))?.label;
      if (label) parts.push(label);
    }
    if (widget.type === 'kpi' && !mine) {
      const metricLabel = KPI_METRIC_OPTIONS.find((m) => m.value === widget.config?.metric)?.label;
      if (metricLabel) parts.push(metricLabel);
    }
    if (isTradeWatcherWidgetType(widget.type)) {
      parts.push('NinjaTrader');
    } else if (mine) {
      parts.push(widget.config?.account ? describeAccountChoice(widget.config.account) : 'My accounts');
    } else if (widget.config?.strategy) {
      // Resolve `custom:<id>` to the combo's friendly name so the row reads
      // as the user-given combo title instead of an opaque id.
      const raw = widget.config.strategy;
      if (typeof raw === 'string' && raw.startsWith('custom:')) {
        const cid = raw.slice('custom:'.length);
        const combo = (customCombos || []).find((c) => c && c.id === cid);
        parts.push(combo ? combo.name : 'Custom strat combo (deleted)');
      } else {
        parts.push(raw);
      }
    }
    const subtitle = parts.slice(1).join(' · ');
    return (
      <SortableCustomizeRow
        key={id}
        id={id}
        label={meta?.label || widget.type}
        subtitle={subtitle}
        onHide={() => onToggleVisibility(id, true)}
      />
    );
  };

  return (
    <div className="dashboard-customize">
      <div className="dashboard-customize-header">
        <div>
          <h3>Customize Dashboard</h3>
          <p>Drag to reorder · Hide / show widgets · Add new ones</p>
          <p className="dashboard-customize-mobile-hint">
            Tip: the order set here controls how widgets stack on mobile. On
            desktop you can drag widgets freely on the grid instead.
          </p>
        </div>
        <button
          type="button"
          className="dashboard-customize-close"
          onClick={onClose}
          aria-label="Close customize"
        >
          <IconX size={16} />
        </button>
      </div>

      {layout.order.length > 0 ? (
        <div className="dashboard-customize-section">
          <div className="dashboard-customize-section-title">Active widgets</div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={layout.order} strategy={verticalListSortingStrategy}>
              <div className="dashboard-customize-list">
                {layout.order.map(renderRow)}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div className="dashboard-customize-section">
          <div className="dashboard-customize-section-title">Active widgets</div>
          <div className="dashboard-customize-empty">No active widgets. Add one below.</div>
        </div>
      )}

      {layout.hidden.length > 0 ? (
        <div className="dashboard-customize-section">
          <div className="dashboard-customize-section-title">Hidden widgets</div>
          <div className="dashboard-customize-hidden-grid">
            {layout.hidden.map((id) => {
              const widget = widgetsById.get(id);
              if (!widget) return null;
              const meta = getWidgetMeta(widget.type);
              // Same "metric · strategy" disambiguation as the active list.
              const hiddenSubParts = [];
              if (widget.type === 'kpi') {
                const metricLabel = KPI_METRIC_OPTIONS.find((m) => m.value === widget.config?.metric)?.label;
                if (metricLabel) hiddenSubParts.push(metricLabel);
              }
              if (widget.config?.strategy) {
                const raw = widget.config.strategy;
                if (typeof raw === 'string' && raw.startsWith('custom:')) {
                  const cid = raw.slice('custom:'.length);
                  const combo = (customCombos || []).find((c) => c && c.id === cid);
                  hiddenSubParts.push(combo ? combo.name : 'Custom strat combo (deleted)');
                } else {
                  hiddenSubParts.push(raw);
                }
              }
              return (
                <div key={id} className="dashboard-customize-hidden-item">
                  <button
                    type="button"
                    className="dashboard-customize-hidden-show"
                    onClick={() => onToggleVisibility(id, false)}
                    title="Show widget"
                  >
                    <IconEye size={14} />
                    <span>
                      <strong>{meta?.label || widget.type}</strong>
                      {hiddenSubParts.length > 0 ? <em>{hiddenSubParts.join(' · ')}</em> : null}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="dashboard-customize-hidden-remove"
                    onClick={() => onRemove(id)}
                    title="Remove widget permanently"
                    aria-label="Remove widget"
                  >
                    <IconTrash size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="dashboard-customize-section">
        <button
          type="button"
          className="dashboard-customize-add"
          onClick={onAddWidget}
        >
          <IconPlus size={14} /> Add new widget
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  GridWidget — explicit-placement wrapper with grip / hide / resize handles  */
/* ─────────────────────────────────────────────────────────────────────────── */

function GridWidget({
  id,
  customizing,
  placement,         // {col, row, w, h}
  minW,
  minH,
  widgetType,        // widget.type — used for mobile aspect adjustments
  widgetConfig,      // widget.config — used to read KPI `metric` on mobile
  cellMetricsRef,    // ref → {cellW, rowH, gap} for snapping resize math
  onPlacementChange, // (patch) → void  (clamps + collision-checks upstream)
  onHide,
  onEdit,            // optional — opens the edit modal for this widget
  onDuplicate,       // optional — clones this widget into the next free slot
  children,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const resizeRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);

  const { col, row, w, h } = placement;

  // Single-metric KPI cards (Net Total / Win Rate / Best Day) are 2x2 on
  // desktop — square. On mobile they collapse to 1 column (~170px wide) but
  // only h=2 rows (~120px tall), which leaves the card noticeably squat. Bump
  // the mobile row span so they render closer to a square. Week Win Rate
  // (h=2, w=12 → full row of 5 small cards) is intentionally left thin.
  const isSingleMetricKpi = widgetType === 'kpi' && widgetConfig?.metric !== 'weekWinRate';
  const mobileH = Math.max(isSingleMetricKpi ? 3 : 1, MOBILE_MIN_ROWS[widgetType] || 0, h);
  const mobileCols = MOBILE_HALF_WIDTH_WIDGET_TYPES.has(widgetType) && w <= 4
    && !MOBILE_FULL_WIDTH_WIDGET_TYPES.has(widgetType) ? 1 : 2;

  const style = {
    gridColumn: `${col} / span ${w}`,
    gridRow: `${row} / span ${h}`,
    /*
     * Mobile sizing vars consumed by the 2-col mobile grid override (see CSS).
     *  - `--widget-mobile-cols` (1 or 2): KPI-sized widgets sit 50% wide; calendars,
     *    charts, and tables always span the full row (2 cols).
     *  - `--widget-mobile-h`: row span on mobile, follows the configured h so a tall
     *    desktop widget (e.g. Calendar at h=6) is still tall on mobile, just at the
     *    smaller mobile row height. Body scrolls internally if content overflows.
     */
    '--widget-mobile-cols': mobileCols,
    '--widget-mobile-h': mobileH,
    transform: CSS.Translate.toString(transform),
    // Disable transitions while actively dragging or resizing to avoid lag against the cursor.
    transition: isDragging || isResizing ? 'none' : undefined,
    zIndex: isDragging ? 50 : undefined,
  };

  /**
   * `mode` describes which edge(s) the user grabbed. Each edge token in the
   * string makes that edge follow the cursor; the opposite edge stays anchored:
   *   - 'right'        → right edge moves (width grows/shrinks)
   *   - 'left'         → left edge moves (col + width change, right edge fixed)
   *   - 'bottom'       → bottom edge moves (height grows/shrinks)
   *   - 'top'          → top edge moves (row + height change, bottom edge fixed)
   *   - 'bottom-right' → both right & bottom move (top-left corner anchored)
   *   - 'bottom-left'  → both left & bottom move
   *   - 'top-right'    → both top & right move
   *   - 'top-left'     → both top & left move
   */
  const startResize = useCallback((e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startCol: col,
      startRow: row,
      startW: w,
      startH: h,
      lastEmitted: { col, row, w, h },
      mode,
    };
    setIsResizing(true);
  }, [col, row, w, h]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMove = (e) => {
      const r = resizeRef.current;
      if (!r) return;
      const metrics = cellMetricsRef.current || { cellW: 80, rowH: ROW_PX, gap: GRID_GAP_PX };
      const wStep = metrics.cellW + metrics.gap;
      const hStep = metrics.rowH + metrics.gap;
      const dCols = Math.round((e.clientX - r.startX) / Math.max(1, wStep));
      const dRows = Math.round((e.clientY - r.startY) / Math.max(1, hStep));

      let nextCol = r.startCol;
      let nextRow = r.startRow;
      let nextW = r.startW;
      let nextH = r.startH;

      // Horizontal: right edge grows/shrinks OR left edge moves with the right
      // edge anchored at startCol + startW.
      if (r.mode.includes('right')) {
        nextW = Math.max(minW, Math.min(GRID_TRACKS - r.startCol + 1, r.startW + dCols));
      } else if (r.mode.includes('left')) {
        const rightAnchor = r.startCol + r.startW;
        nextCol = Math.max(1, Math.min(rightAnchor - minW, r.startCol + dCols));
        nextW = rightAnchor - nextCol;
      }

      // Vertical: same dance for bottom/top edges.
      if (r.mode.includes('bottom')) {
        nextH = Math.max(minH, Math.min(MAX_ROW_SPAN, r.startH + dRows));
      } else if (r.mode.includes('top')) {
        const bottomAnchor = r.startRow + r.startH;
        nextRow = Math.max(1, Math.min(bottomAnchor - minH, r.startRow + dRows));
        nextH = bottomAnchor - nextRow;
      }

      const patch = {};
      if (nextCol !== r.lastEmitted.col) { patch.col = nextCol; r.lastEmitted.col = nextCol; }
      if (nextRow !== r.lastEmitted.row) { patch.row = nextRow; r.lastEmitted.row = nextRow; }
      if (nextW !== r.lastEmitted.w) { patch.w = nextW; r.lastEmitted.w = nextW; }
      if (nextH !== r.lastEmitted.h) { patch.h = nextH; r.lastEmitted.h = nextH; }

      if (Object.keys(patch).length > 0) onPlacementChange(patch);
    };
    const handleUp = () => {
      resizeRef.current = null;
      setIsResizing(false);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isResizing, onPlacementChange, cellMetricsRef, minW, minH]);

  const cls = [
    'dashboard-widget-wrapper',
    customizing ? 'is-customizing' : '',
    isDragging ? 'is-dragging' : '',
    isResizing ? 'is-resizing' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={setNodeRef} style={style} className={cls}>
      {customizing ? (
        <>
          <button
            type="button"
            className="dashboard-widget-grip"
            {...attributes}
            {...listeners}
            aria-label="Drag widget"
            title="Drag to move"
          >
            <IconGrip size={16} />
          </button>
          <div className="dashboard-widget-actions">
            {onEdit ? (
              <button
                type="button"
                className="dashboard-widget-action"
                onClick={onEdit}
                aria-label="Edit widget"
                title="Edit widget"
              >
                <IconPencil size={14} />
              </button>
            ) : null}
            {onDuplicate ? (
              <button
                type="button"
                className="dashboard-widget-action"
                onClick={onDuplicate}
                aria-label="Duplicate widget"
                title="Duplicate widget"
              >
                <IconCopy size={14} />
              </button>
            ) : null}
            <button
              type="button"
              className="dashboard-widget-action dashboard-widget-action-danger"
              onClick={onHide}
              aria-label="Hide widget"
              title="Hide widget"
            >
              <IconEyeOff size={14} />
            </button>
          </div>
          {/* Bottom edge (height resize, anchored at top). */}
          <div
            onMouseDown={(e) => startResize(e, 'bottom')}
            className="dashboard-widget-resize dashboard-widget-resize-v dashboard-widget-resize-bottom"
            title={`Drag to resize height (${h} row${h === 1 ? '' : 's'})`}
          >
            <span />
          </div>
          {/* Top edge (height resize, anchored at bottom). */}
          <div
            onMouseDown={(e) => startResize(e, 'top')}
            className="dashboard-widget-resize dashboard-widget-resize-v dashboard-widget-resize-top"
            title={`Drag to resize height from top (${h} row${h === 1 ? '' : 's'})`}
          >
            <span />
          </div>
          {/* Right edge (width resize, anchored at left). */}
          <div
            onMouseDown={(e) => startResize(e, 'right')}
            className="dashboard-widget-resize dashboard-widget-resize-h dashboard-widget-resize-right"
            title={`Drag to resize width (${w}/${GRID_TRACKS} columns)`}
          >
            <span />
          </div>
          {/* Left edge (width resize, anchored at right). */}
          <div
            onMouseDown={(e) => startResize(e, 'left')}
            className="dashboard-widget-resize dashboard-widget-resize-h dashboard-widget-resize-left"
            title={`Drag to resize width from left (${w}/${GRID_TRACKS} columns)`}
          >
            <span />
          </div>
          {/* Bottom-right corner (two-axis resize, top-left anchored). */}
          <div
            onMouseDown={(e) => startResize(e, 'bottom-right')}
            className="dashboard-widget-resize dashboard-widget-resize-corner dashboard-widget-resize-corner-br"
            title="Drag to resize width and height"
            aria-hidden
          />
          {/* Bottom-left corner (two-axis resize, top-right anchored). The
              top corners are skipped because the drag grip + action toolbar
              already live there. */}
          <div
            onMouseDown={(e) => startResize(e, 'bottom-left')}
            className="dashboard-widget-resize dashboard-widget-resize-corner dashboard-widget-resize-corner-bl"
            title="Drag to resize width and height from the left"
            aria-hidden
          />
        </>
      ) : null}
      <div className="dashboard-widget-wrapper-inner">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Add widget modal                                                           */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Modal that adds a new widget OR edits an existing one. Pass `existingWidget` to put
 * the modal in edit mode — the type picker is skipped, fields are prefilled with the
 * widget's current config, and the save button text reflects the action.
 */
const COMBO_WEEKDAYS = [
  { key: 'monday', short: 'Mon' },
  { key: 'tuesday', short: 'Tue' },
  { key: 'wednesday', short: 'Wed' },
  { key: 'thursday', short: 'Thu' },
  { key: 'friday', short: 'Fri' },
];

function AddWidgetModal({ isOpen, onClose, onSave, sheetNames, customCombos, onCreateCombo, comboLimitReached = false, loadingSheets, existingWidget, widgetTypeGroups = [], companyDashboard = false }) {
  const editMode = !!existingWidget;
  const [step, setStep] = useState(editMode ? 'config' : 'type');
  const [pickedType, setPickedType] = useState(editMode ? existingWidget.type : null);
  const [config, setConfig] = useState(editMode ? { ...(existingWidget.config || {}) } : {});
  const [error, setError] = useState(null);
  // Inline "mix strategies" combo builder (Calendar widget only).
  const [comboBuilderOpen, setComboBuilderOpen] = useState(false);
  const [comboName, setComboName] = useState('');
  const [comboSleeves, setComboSleeves] = useState([{ strategy: '', days: [], size: 1 }]);
  const [comboSaving, setComboSaving] = useState(false);
  const [comboError, setComboError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (editMode) {
      setStep('config');
      setPickedType(existingWidget.type);
      // Fill in settings added since the widget was made (e.g. Data source).
      const defs = getWidgetMeta(existingWidget.type)?.defaultConfig?.() || {};
      setConfig({ ...defs, ...(existingWidget.config || {}) });
    } else {
      setStep('type');
      setPickedType(null);
      setConfig({});
    }
    setError(null);
    setComboBuilderOpen(false);
    setComboName('');
    setComboSleeves([{ strategy: '', days: [], size: 1 }]);
    setComboSaving(false);
    setComboError(null);
  // We deliberately rerun on the widget identity, not its config object: opening the
  // modal for the same widget shouldn't blow away in-progress edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editMode, existingWidget?.id]);

  if (!isOpen) return null;

  const meta = pickedType ? getWidgetMeta(pickedType) : null;
  // Strategy dropdown lists user's Custom Multi-Strat combos at the top
  // (as {value: 'custom:<id>', label: comboName}), followed by their real
  // strategies. The widget config dropdown renders both via the same
  // {value,label} shape; the data layer detects `custom:<id>` and routes
  // to the combo-data endpoint.
  const strategiesProvider = () => {
    const comboOptions = (customCombos || []).map((c) => ({
      value: `custom:${c.id}`,
      label: c.name,
    }));
    const regular = regularSheetNamesOnly(sheetNames);
    return [...comboOptions, ...regular];
  };
  const fieldCtx = { companyDashboard };
  // Fields with showWhen only appear (and are only required) when it holds.
  const fields = (meta ? meta.configFields(strategiesProvider, fieldCtx) : [])
    .filter((f) => !f.showWhen || f.showWhen(config));
  const usingMyAccounts = config.source === 'mine';

  const handlePickType = (type) => {
    const next = getWidgetMeta(type);
    if (!next) return;
    const defaults = next.defaultConfig ? next.defaultConfig() : {};
    const fields = next.configFields ? next.configFields(strategiesProvider, fieldCtx) : [];
    if (fields.length === 0) {
      onSave({ type, config: defaults });
      return;
    }
    setPickedType(type);
    setConfig({ ...defaults });
    setStep('config');
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!meta) return;
    for (const field of fields) {
      if (field.required && !String(config[field.key] || '').trim()) {
        setError(`${field.label} is required.`);
        return;
      }
    }
    onSave({ type: pickedType, config });
  };

  const updateField = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  // ── Inline combo builder (Calendar widget) ──────────────────────────────
  // Real strategies only (no demo / synthetic / existing combos as sleeves).
  const comboStrategyOptions = regularSheetNamesOnly(sheetNames).filter(
    (s) => s
      && s !== 'Sample Strategy (Demo)'
      && s !== 'Multi-Strat'
      && s !== 'Vector Master Strat'
      && !(typeof s === 'string' && s.startsWith('custom:')),
  );

  const updateSleeve = (idx, patch) => {
    setComboSleeves((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    setComboError(null);
  };
  const toggleSleeveDay = (idx, dayKey) => {
    setComboSleeves((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const has = s.days.includes(dayKey);
      return { ...s, days: has ? s.days.filter((d) => d !== dayKey) : [...s.days, dayKey] };
    }));
    setComboError(null);
  };
  const addSleeve = () => setComboSleeves((prev) => (prev.length >= 12 ? prev : [...prev, { strategy: '', days: [], size: 1 }]));
  const removeSleeve = (idx) => setComboSleeves((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

  const handleSaveCombo = async () => {
    const trimmed = comboName.trim();
    if (!trimmed) { setComboError('Give the calendar mix a name.'); return; }
    const filled = comboSleeves.filter((s) => s.strategy && s.days.length > 0);
    if (filled.length === 0) { setComboError('Add at least one strategy with at least one active weekday.'); return; }
    if (typeof onCreateCombo !== 'function') { setComboError('Saving is unavailable here.'); return; }
    setComboSaving(true);
    setComboError(null);
    const payload = {
      name: trimmed.slice(0, 80),
      sleeves: filled.map((s) => {
        const raw = Number(s.size);
        const intSize = Number.isFinite(raw) && raw >= 1 ? Math.min(Math.max(Math.round(raw), 1), 100) : 1;
        return { strategy: s.strategy, days: s.days, size: intSize };
      }),
    };
    const result = await onCreateCombo(payload);
    setComboSaving(false);
    if (result && typeof result === 'object' && result.error) {
      setComboError(result.error);
      return;
    }
    if (typeof result === 'string') {
      // Point this widget at the freshly-created combo and collapse the builder.
      updateField('strategy', result);
      setComboBuilderOpen(false);
    }
  };

  const headerTitle = step === 'type'
    ? 'Add a widget'
    : `${editMode ? 'Edit' : 'Configure'} ${meta?.label || 'widget'}`;
  const submitLabel = editMode ? 'Save changes' : 'Add widget';

  return (
    <div className="dashboard-modal-overlay" role="dialog" aria-modal="true">
      <div className="dashboard-modal">
        <div className="dashboard-modal-header">
          <h3>{headerTitle}</h3>
          <button type="button" className="dashboard-modal-close" onClick={onClose} aria-label="Close">
            <IconX size={18} />
          </button>
        </div>

        {step === 'type' ? (
          <div className="dashboard-modal-body">
            <p className="dashboard-modal-hint">Pick a widget type to add to this dashboard.</p>
            {widgetTypeGroups.map((group) => (
              <div key={group.id} className="dashboard-widget-type-group">
                <h4 className="dashboard-widget-type-group-title">{group.label}</h4>
                <div className="dashboard-widget-type-grid">
                  {group.items.map((t) => (
                    <button
                      key={t.type}
                      type="button"
                      className="dashboard-widget-type-card"
                      onClick={() => handlePickType(t.type)}
                    >
                      <strong>{t.label}</strong>
                      <span>{t.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <form className="dashboard-modal-body" onSubmit={handleSubmit}>
            {fields.map((field) => {
              const value = config[field.key] ?? '';
              if (field.type === 'select') {
                // optionsProvider can return strings OR {value, label} objects
                // — the latter so dashboards can surface user Custom Multi-
                // Strat combos with a friendly label distinct from their
                // internal `custom:<id>` value.
                const options = typeof field.optionsProvider === 'function'
                  ? field.optionsProvider().map((s) => (
                    typeof s === 'string'
                      ? { value: s, label: s }
                      : { value: s?.value, label: s?.label || s?.value }
                  ))
                  : (field.options || []);
                return (
                  <label key={field.key} className="dashboard-modal-field">
                    <span>{field.label}{field.required ? ' *' : ''}</span>
                    <select
                      value={value}
                      onChange={(e) => updateField(field.key, e.target.value)}
                      disabled={field.required && options.length === 0 && loadingSheets}
                    >
                      {field.noBlank ? null : <option value="">{field.required ? 'Select…' : '—'}</option>}
                      {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </label>
                );
              }
              if (field.type === 'number') {
                return (
                  <label key={field.key} className="dashboard-modal-field">
                    <span>{field.label}</span>
                    <input
                      type="number"
                      min={field.min ?? undefined}
                      max={field.max ?? undefined}
                      value={value}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') updateField(field.key, '');
                        else updateField(field.key, Number(raw));
                      }}
                    />
                  </label>
                );
              }
              return (
                <label key={field.key} className="dashboard-modal-field">
                  <span>{field.label}</span>
                  <input
                    type="text"
                    value={value}
                    maxLength={field.maxLength ?? undefined}
                    onChange={(e) => updateField(field.key, e.target.value)}
                  />
                </label>
              );
            })}

            {/* Calendar + Cumulative Chart widgets: build a custom mix of strategies inline */}
            {(pickedType === 'calendar' || pickedType === 'weeklyChart') && !usingMyAccounts && typeof onCreateCombo === 'function' ? (
              <div className="dashboard-combo-builder">
                {!comboBuilderOpen ? (
                  <button
                    type="button"
                    className="dashboard-combo-builder-open"
                    onClick={() => setComboBuilderOpen(true)}
                    disabled={comboLimitReached}
                    title={comboLimitReached ? `You already have ${MAX_CUSTOM_COMBOS} saved strat combos (the max). Delete one in ${getResultsNavLabel()} to free a slot.` : undefined}
                  >
                    <IconPlus size={14} /> Build a custom strategy mix for this {pickedType === 'weeklyChart' ? 'chart' : 'calendar'}
                  </button>
                ) : (
                  <div className="dashboard-combo-builder-panel">
                    <div className="dashboard-combo-builder-title">
                      Mix strategies by weekday
                      <span className="dashboard-combo-builder-sub">P&amp;L is summed across active sleeves, scaled by each size.</span>
                    </div>
                    <label className="dashboard-modal-field">
                      <span>Mix name *</span>
                      <input
                        type="text"
                        value={comboName}
                        maxLength={80}
                        placeholder="e.g. My GFPB + Scarberia stack"
                        onChange={(e) => { setComboName(e.target.value); setComboError(null); }}
                      />
                    </label>
                    {comboSleeves.map((sleeve, idx) => (
                      <div key={idx} className="dashboard-combo-sleeve">
                        <select
                          className="dashboard-combo-sleeve-strategy"
                          value={sleeve.strategy}
                          onChange={(e) => updateSleeve(idx, { strategy: e.target.value })}
                        >
                          <option value="">Select strategy…</option>
                          {comboStrategyOptions.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <div className="dashboard-combo-sleeve-days">
                          {COMBO_WEEKDAYS.map((d) => (
                            <button
                              key={d.key}
                              type="button"
                              className={`dashboard-combo-day${sleeve.days.includes(d.key) ? ' is-on' : ''}`}
                              onClick={() => toggleSleeveDay(idx, d.key)}
                              aria-pressed={sleeve.days.includes(d.key)}
                            >
                              {d.short}
                            </button>
                          ))}
                        </div>
                        <div className="dashboard-combo-sleeve-size">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={sleeve.size}
                            aria-label="Size multiplier"
                            onChange={(e) => updateSleeve(idx, { size: e.target.value === '' ? '' : Number(e.target.value) })}
                          />
                          <span>×</span>
                        </div>
                        <button
                          type="button"
                          className="dashboard-combo-sleeve-remove"
                          onClick={() => removeSleeve(idx)}
                          disabled={comboSleeves.length === 1}
                          aria-label="Remove strategy"
                        >
                          <IconTrash size={14} />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="dashboard-combo-add-sleeve" onClick={addSleeve}>
                      <IconPlus size={13} /> Add another strategy
                    </button>
                    {comboError ? <div className="dashboard-modal-error" role="alert">{comboError}</div> : null}
                    <div className="dashboard-combo-builder-actions">
                      <button type="button" className="dashboard-modal-btn" onClick={() => setComboBuilderOpen(false)} disabled={comboSaving}>
                        Cancel
                      </button>
                      <button type="button" className="dashboard-modal-btn dashboard-modal-btn-primary" onClick={handleSaveCombo} disabled={comboSaving}>
                        {comboSaving ? 'Saving…' : 'Save mix & use it'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {error ? <div className="dashboard-modal-error" role="alert">{error}</div> : null}
            <div className="dashboard-modal-footer">
              {editMode ? (
                <button type="button" className="dashboard-modal-btn" onClick={onClose}>
                  Cancel
                </button>
              ) : (
                <button type="button" className="dashboard-modal-btn" onClick={() => setStep('type')}>
                  Back
                </button>
              )}
              <button type="submit" className="dashboard-modal-btn dashboard-modal-btn-primary">
                {submitLabel}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ConfirmModal({ isOpen, title, message, confirmLabel = 'Confirm', onConfirm, onCancel, danger }) {
  if (!isOpen) return null;
  return (
    <div className="dashboard-modal-overlay" role="dialog" aria-modal="true">
      <div className="dashboard-modal dashboard-modal-confirm">
        <div className="dashboard-modal-header">
          <h3>{title}</h3>
          <button type="button" className="dashboard-modal-close" onClick={onCancel} aria-label="Close">
            <IconX size={18} />
          </button>
        </div>
        <div className="dashboard-modal-body">
          <p>{message}</p>
        </div>
        <div className="dashboard-modal-footer">
          <button type="button" className="dashboard-modal-btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className={`dashboard-modal-btn ${danger ? 'dashboard-modal-btn-danger' : 'dashboard-modal-btn-primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main page                                                                  */
/* ─────────────────────────────────────────────────────────────────────────── */

function DashboardPageInner({
  user,
  sheetNames,
  customCombos,
  onCustomComboUpsert,
  loadingSheets,
  canCustomize,
  canEditVector,
  isAdmin,
  isVectorAdmin,
  onNavigate,
  onUpgrade,
  suppressPageHeading = false,
  // People with no company: only their own dashboards (no company dashboard, no
  // company-strategy widgets), and starter templates when they have none yet.
  personalOnly = false,
  // Personal mode: told how many dashboards the user has (the welcome page steps
  // back once there is one).
  onPersonalCountChange,
}) {
  const email = user?.email || '';

  // Self users (personalOnly) must never see a company dashboard, not even for a
  // moment from the browser cache (the cache is per email, shared with company
  // mode), so in this mode company dashboards are dropped from what's shown.
  const isCompanyDashboard = (d) => Boolean(d && (isVectorDashboardId(d.id) || d.is_system));
  const initialCachedDashboard = (() => {
    const d = hydrateDashboardFromCache(email);
    return personalOnly && isCompanyDashboard(d) ? null : d;
  })();
  const [dashboardsState, setDashboards] = useState(() => readCachedDashboardList(email) || []);
  const [currentState, setCurrent] = useState(initialCachedDashboard);
  const dashboards = useMemo(
    () => (personalOnly ? dashboardsState.filter((d) => !isCompanyDashboard(d)) : dashboardsState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardsState, personalOnly],
  );
  const current = personalOnly && isCompanyDashboard(currentState) ? null : currentState;
  const [loading, setLoading] = useState(!initialCachedDashboard);
  const [pageError, setPageError] = useState(null);
  const [customizing, setCustomizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [editWidgetId, setEditWidgetId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  // Loaded once on mount. When `published_at` is non-null, Vector renders with
  // the server-stored layout (overriding the code defaults in
  // vectorDashboardTemplate.js). Admin/VectorAdmin edits write back through
  // apiPutDefaultTemplate and update this state in place. Holds the pinned
  // Vector message in `vector_message` (string or null).
  const [vectorTemplate, setVectorTemplate] = useState(null);

  const lastSavedRef = useRef(null);
  const saveTimerRef = useRef(null);
  // Bumped on every local layout/widgets mutation so a save that is in flight
  // can detect whether the user has made further edits since it kicked off; if
  // so, we leave local state alone and rely on the next save to catch up.
  const layoutSeqRef = useRef(0);
  // Ref instead of dep so the many setCurrent callbacks below can read the
  // latest value without each one recreating when canEditVector toggles.
  const canEditVectorRef = useRef(canEditVector);
  canEditVectorRef.current = canEditVector;

  /* ── Initial load: seed from cache, then refresh from server ─────────────── */
  useEffect(() => {
    if (!email) {
      setLoading(false);
      setPageError(null);
      return undefined;
    }

    let cancelled = false;
    // Snapshot the layout-mutation counter at mount; if the user makes any local
    // edits before the background fetch returns, we leave their state alone.
    const seqAtMount = layoutSeqRef.current;
    const userEditedSinceMount = () => layoutSeqRef.current !== seqAtMount;

    // Basic-tier (read-only) users always get the org-wide Vector Dashboard as
    // their only dashboard. We still fetch the server template so they see the
    // latest VectorAdmin-published version; code defaults are the fallback.
    // Self users have no company dashboard to fall back to.
    if (!canCustomize && personalOnly) {
      setDashboards([]);
      setCurrent(null);
      setLoading(false);
      return undefined;
    }
    if (!canCustomize) {
      (async () => {
        let template = null;
        try { template = await apiGetDefaultTemplate(); } catch (_err) { /* fall back to code */ }
        if (cancelled) return;
        if (template) setVectorTemplate(template);
        const synthetic = resolveVectorDashboard(email, template, { markDefault: true });
        setDashboards([{
          id: synthetic.id,
          name: synthetic.name,
          is_default: true,
          is_system: true,
        }]);
        setCurrent(synthetic);
        rememberVectorDashboard(email, synthetic);
        setLoading(false);
      })();
      return () => { cancelled = true; };
    }

    // Vector is the default unless the user has explicitly promoted one of
    // their personal dashboards via "Set as default". This keeps a single
    // source of truth — the server's `is_default` column — and lets the user
    // switch the default freely while still falling back to Vector when none
    // is set.
    const mergeWithVector = (personalList) => {
      const filtered = personalList.filter((d) => !isVectorDashboardId(d.id));
      if (personalOnly) return filtered;
      const hasPersonalDefault = filtered.some((d) => d.is_default);
      return [
        {
          id: VECTOR_DASHBOARD_ID,
          name: orgDashboardName(),
          is_default: !hasPersonalDefault,
          is_system: true,
        },
        ...filtered,
      ];
    };

    // 1) Hydrate from cache for instant render
    const cachedList = readCachedDashboardList(email);
    if (cachedList) {
      const merged = mergeWithVector(cachedList);
      setDashboards(merged);
      const activeId = readActiveDashboardId(email);
      const target = (activeId && merged.find((d) => d.id === activeId))
        || merged.find((d) => d.is_default)
        || merged[0];
      if (target) {
        if (isVectorDashboardId(target.id)) {
          // Cache first — code defaults include every widget (e.g. Top bar) and
          // flash briefly until the published template arrives from the server.
          const synthetic = resolveVectorDashboard(email, null, { markDefault: target.is_default });
          setCurrent(synthetic);
          // Editors get a rollback target; non-editors don't save anyway.
          lastSavedRef.current = canEditVector
            ? { layout: synthetic.layout, widgets: synthetic.widgets || [] }
            : null;
          setLoading(false);
        } else {
          const cachedDash = readCachedDashboard(email, target.id);
          if (cachedDash) {
            const normalized = normalizeDashboardLayout(cachedDash);
            setCurrent(normalized);
            lastSavedRef.current = { layout: normalized.layout, widgets: normalized.widgets || [] };
            setLoading(false);
          }
        }
      }
    }

    // 2) Refresh from server in background — fetch list AND the Vector
    // template in parallel so an Admin/VectorAdmin's published layout shows
    // immediately on every user's next load.
    (async () => {
      try {
        setPageError(null);
        const [listResult, templateResult] = await Promise.all([
          apiList(),
          apiGetDefaultTemplate().catch((err) => {
            console.warn('Failed to load Vector template:', err);
            return null;
          }),
        ]);
        if (cancelled) return;
        let list = listResult;
        if (templateResult) setVectorTemplate(templateResult);

        // One-time migration: demote legacy personal defaults so Vector is the
        // org-wide default. See vectorDefaultMigration.js.
        if (!hasMigratedToVectorDefault(email)) {
          list = await migratePersonalDefaultsToVector(email, list);
          if (cancelled) return;
        }

        const merged = mergeWithVector(list);
        setDashboards(merged);
        writeCachedDashboardList(email, list);

        // New users (no personal dashboards) land on the Vector Dashboard by
        // default. We deliberately do NOT auto-create a personal "My Dashboard"
        // anymore — users opt in by clicking "New dashboard" when they want one.
        if (list.length === 0) {
          if (personalOnly) {
            // Nothing yet: the page offers starter templates.
            if (!userEditedSinceMount()) setCurrent(null);
            setLoading(false);
            return;
          }
          if (!userEditedSinceMount()) {
            const synthetic = resolveVectorDashboard(email, templateResult, { markDefault: true });
            setCurrent(synthetic);
            rememberVectorDashboard(email, synthetic);
            writeActiveDashboardId(email, VECTOR_DASHBOARD_ID);
            lastSavedRef.current = null;
          }
          setLoading(false);
          return;
        }

        const activeId = readActiveDashboardId(email);
        // Vector is the default. Only switch away when the user has explicitly
        // chosen another dashboard in this browser (activeId stored AND it
        // still exists on the server).
        const matchedPersonal = activeId && !isVectorDashboardId(activeId)
          ? list.find((d) => d.id === activeId)
          : null;
        let targetId = matchedPersonal ? matchedPersonal.id : VECTOR_DASHBOARD_ID;
        if (personalOnly && targetId === VECTOR_DASHBOARD_ID) {
          targetId = (list.find((d) => d.is_default) || list[0]).id;
        }

        if (targetId === VECTOR_DASHBOARD_ID) {
          if (!userEditedSinceMount()) {
            const hasPersonalDefault = list.some(
              (d) => d.is_default && !isVectorDashboardId(d.id),
            );
            const synthetic = resolveVectorDashboard(
              email,
              templateResult,
              { markDefault: !hasPersonalDefault },
            );
            setCurrent(synthetic);
            rememberVectorDashboard(email, synthetic);
            writeActiveDashboardId(email, VECTOR_DASHBOARD_ID);
            lastSavedRef.current = null;
          }
          setLoading(false);
          return;
        }

        const full = await apiGet(targetId);
        if (cancelled) return;
        // If the user has started editing since this fetch kicked off, don't
        // overwrite their state with the server's older snapshot — their next
        // save will reconcile.
        if (!userEditedSinceMount()) {
          const normalized = normalizeDashboardLayout(full);
          setCurrent(normalized);
          writeCachedDashboard(email, normalized);
          writeActiveDashboardId(email, normalized.id);
          lastSavedRef.current = { layout: normalized.layout, widgets: normalized.widgets || [] };
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        // If we already have a cached current, don't blow away the page on a refresh failure.
        if (!current) {
          setPageError(err?.message || 'Failed to load dashboards');
        }
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, canCustomize, personalOnly]);

  useEffect(() => {
    if (!personalOnly || !onPersonalCountChange || loading) return;
    onPersonalCountChange(dashboards.filter((d) => !d.is_system && !isVectorDashboardId(d.id)).length);
  }, [personalOnly, onPersonalCountChange, loading, dashboards]);

  /* ── Persist current dashboard (debounced) ──────────────────────────────── */
  const persistDashboard = useCallback(async (next) => {
    if (!next?.id) return;
    // Basic-tier users see a synthetic, read-only template — never write to the server.
    if (!canCustomize) return;
    const isVector = isVectorDashboardId(next.id) || next?.is_system;
    // Vector edits go to the org-wide template endpoint (Admin/VectorAdmin
    // gated server-side). Skip entirely if the current user can't edit Vector.
    if (isVector) {
      if (!canEditVectorRef.current) return;
      const seqAtStart = layoutSeqRef.current;
      setSaving(true);
      setSaveError(null);
      try {
        const updated = await apiPutDefaultTemplate({
          layout: next.layout,
          widgets: next.widgets,
        });
        // Keep the in-memory template fresh so subsequent re-renders / switch
        // events use the version we just published, not a stale cache. We
        // merge into the previous template so unrelated fields (e.g. the
        // pinned vector_message) aren't dropped.
        setVectorTemplate((prev) => ({
          ...(prev || {}),
          layout: updated?.layout || next.layout,
          widgets: updated?.widgets || next.widgets,
          vector_message: Object.prototype.hasOwnProperty.call(updated || {}, 'vector_message')
            ? updated.vector_message
            : (prev?.vector_message ?? null),
          published_at: updated?.published_at || new Date().toISOString(),
        }));
        lastSavedRef.current = { layout: next.layout, widgets: next.widgets || [] };
        rememberVectorDashboard(email, next);
        setSaving(false);
      } catch (err) {
        setSaving(false);
        setSaveError(err?.message || `Failed to save ${getEntityDisplayName()} Dashboard`);
        if (
          layoutSeqRef.current === seqAtStart
          && lastSavedRef.current
          && current?.id === next.id
        ) {
          setCurrent((prev) => (prev ? {
            ...prev,
            layout: lastSavedRef.current.layout,
            widgets: lastSavedRef.current.widgets,
          } : prev));
        }
      }
      return;
    }
    const seqAtStart = layoutSeqRef.current;
    setSaving(true);
    setSaveError(null);
    try {
      await apiUpdate(next.id, {
        layout: next.layout,
        widgets: next.widgets,
      });
      // Local state IS the saved state. We deliberately do NOT replace `current`
      // from the server response — that opens us up to clobbering the user's
      // most recent edits if the server round-trips a different shape (e.g. a
      // stale build, a stale request finishing late, or schema validation
      // dropping a field). All we need to do is mark this state as "saved" so
      // we have a known-good rollback target if a future save fails.
      lastSavedRef.current = { layout: next.layout, widgets: next.widgets || [] };
      writeCachedDashboard(email, next);
      setSaving(false);
    } catch (err) {
      setSaving(false);
      setSaveError(err?.message || 'Failed to save layout');
      // Only revert if the user hasn't made further edits since this save
      // kicked off — otherwise we'd undo their most recent change just because
      // an older save round-tripped with an error.
      if (
        layoutSeqRef.current === seqAtStart
        && lastSavedRef.current
        && current?.id === next.id
      ) {
        setCurrent((prev) => (prev ? {
          ...prev,
          layout: lastSavedRef.current.layout,
          widgets: lastSavedRef.current.widgets,
        } : prev));
      }
    }
  }, [email, current?.id, canCustomize]);

  const scheduleSave = useCallback((next) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persistDashboard(next), SAVE_DEBOUNCE_MS);
  }, [persistDashboard]);

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);


  /* ── Dashboard list ops ─────────────────────────────────────────────────── */

  const refreshList = useCallback(async () => {
    try {
      const list = await apiList();
      // Cache the raw server list — we re-derive the Vector entry on read so
      // changes to the template don't have to invalidate caches.
      writeCachedDashboardList(email, list);
      const personal = list.filter((d) => !isVectorDashboardId(d.id));
      const hasPersonalDefault = personal.some((d) => d.is_default);
      const vectorEntry = {
        id: VECTOR_DASHBOARD_ID,
        name: orgDashboardName(),
        is_default: !hasPersonalDefault,
        is_system: true,
      };
      setDashboards([vectorEntry, ...personal]);
    } catch (err) {
      console.warn('Failed to refresh dashboard list:', err);
    }
  }, [email]);

  const switchDashboard = useCallback(async (id) => {
    if (!id || id === current?.id) return;
    setCustomizing(false);
    setLoading(true);

    // Switching to the Vector (system) dashboard — use the in-memory template
    // we loaded at mount (or that an editor just published) plus the code
    // defaults as fallback. Vector owns the Default pill whenever no personal
    // dashboard is flagged is_default.
    if (isVectorDashboardId(id)) {
      const hasPersonalDefault = dashboards.some(
        (d) => d.is_default && !d.is_system && !isVectorDashboardId(d.id),
      );
      const synthetic = resolveVectorDashboard(
        email,
        vectorTemplate,
        { markDefault: !hasPersonalDefault },
      );
      setCurrent(synthetic);
      rememberVectorDashboard(email, synthetic);
      writeActiveDashboardId(email, VECTOR_DASHBOARD_ID);
      lastSavedRef.current = null;
      setLoading(false);
      return;
    }

    const cachedDash = readCachedDashboard(email, id);
    if (cachedDash) {
      const normalized = normalizeDashboardLayout(cachedDash);
      setCurrent(normalized);
      lastSavedRef.current = { layout: normalized.layout, widgets: normalized.widgets || [] };
      writeActiveDashboardId(email, id);
      setLoading(false);
    }

    try {
      const full = await apiGet(id);
      const normalized = normalizeDashboardLayout(full);
      setCurrent(normalized);
      writeCachedDashboard(email, normalized);
      writeActiveDashboardId(email, id);
      lastSavedRef.current = { layout: normalized.layout, widgets: normalized.widgets || [] };
      setLoading(false);
    } catch (err) {
      if (!cachedDash) {
        setPageError(err?.message || 'Failed to load dashboard');
      }
      setLoading(false);
    }
  }, [current?.id, email, dashboards, vectorTemplate]);

  // The RiskLo title / logo opens Overview on the default dashboard, also when
  // Overview is already open on another one.
  const location = useLocation();
  const openDefaultNonce = location.state?.openDefaultDashboard || null;
  const handledDefaultNonceRef = useRef(null);
  useEffect(() => {
    if (!openDefaultNonce || handledDefaultNonceRef.current === openDefaultNonce) return;
    const target = dashboards.find((d) => d.is_default) || dashboards[0];
    if (!target) return;
    handledDefaultNonceRef.current = openDefaultNonce;
    switchDashboard(target.id);
  }, [openDefaultNonce, dashboards, switchDashboard]);

  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  /** "New dashboard": pick a starter (built from your accounts) or start blank. */
  const createDashboard = useCallback(() => {
    const personalCount = dashboards.filter((d) => !d.is_system).length;
    if (personalCount >= MAX_DASHBOARDS) {
      setSaveError(`You can only have ${MAX_DASHBOARDS} dashboards. Delete one before creating another.`);
      return;
    }
    setShowTemplatePicker(true);
  }, [dashboards]);

  const createDashboardFrom = useCallback(async (templateId) => {
    setShowTemplatePicker(false);
    // Personal-only count — the system Vector entry never consumes a slot.
    const personalCount = dashboards.filter((d) => !d.is_system).length;
    if (personalCount >= MAX_DASHBOARDS) {
      setSaveError(`You can only have ${MAX_DASHBOARDS} dashboards. Delete one before creating another.`);
      return;
    }
    // The Master layout's Trade Watcher row only for people with NinjaTrader accounts on it.
    const tradeWatcher = templateId
      ? await loadTwAccounts(email).then((rows) => rows.length > 0).catch(() => false)
      : false;
    let payload = templateId ? buildDashboardFromTemplate(templateId, { tradeWatcher }) : null;
    if (!payload) {
      const name = window.prompt('Name your new dashboard', `Dashboard ${personalCount + 1}`);
      if (!name || !name.trim()) return;
      payload = { name: name.trim(), layout: emptyLayout(), widgets: [] };
    }
    try {
      const created = await apiCreate(payload);
      const normalized = normalizeDashboardLayout(created);
      await refreshList();
      setCurrent(normalized);
      writeCachedDashboard(email, normalized);
      writeActiveDashboardId(email, normalized.id);
      lastSavedRef.current = { layout: normalized.layout, widgets: normalized.widgets || [] };
      // A starter is ready to use; a blank one opens in customize mode.
      setCustomizing(!templateId);
    } catch (err) {
      setSaveError(err?.message || 'Failed to create dashboard');
    }
  }, [dashboards, refreshList, email]);

  // Account choices for "My accounts" widgets (Pro); quietly nothing otherwise.
  useEffect(() => {
    if (canCustomize) loadMyAccounts().catch(() => {});
  }, [canCustomize]);

  // Trade Watcher: offer its widgets on a personal dashboard that has none, for
  // people with NinjaTrader accounts on it (dismissable per dashboard).
  const [hasTradeWatcher, setHasTradeWatcher] = useState(false);
  useEffect(() => {
    if (!canCustomize || !email) return undefined;
    let live = true;
    loadTwAccounts(email).then((rows) => { if (live) setHasTradeWatcher(rows.length > 0); }).catch(() => {});
    return () => { live = false; };
  }, [canCustomize, email]);
  const twDismissKey = `risklo_tw_prompt_dismissed_${email}`;
  const [twDismissed, setTwDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(twDismissKey) || '[]'); } catch (_e) { return []; }
  });
  const dismissTwPrompt = useCallback((dashboardId) => {
    setTwDismissed((prev) => {
      const next = [...new Set([...prev, dashboardId])];
      try { localStorage.setItem(twDismissKey, JSON.stringify(next)); } catch (_e) { /* per-session only */ }
      return next;
    });
  }, [twDismissKey]);
  const addTradeWatcherSection = useCallback(() => {
    if (!current) return;
    const layout = current.layout || emptyLayout();
    const section = tradeWatcherSection();
    const ids = section.widgets.map((w) => w.id);
    const next = {
      ...current,
      widgets: [...section.widgets, ...(current.widgets || [])],
      layout: {
        ...layout,
        order: [...ids, ...(layout.order || [])],
        sizes: { ...section.sizes, ...shiftSizes(layout.sizes, section.height) },
        hidden: [...(layout.hidden || [])],
      },
    };
    layoutSeqRef.current += 1;
    setCurrent(next);
    writeCachedDashboard(email, next);
    persistDashboard(next);
  }, [current, email, persistDashboard]);

  const duplicateCurrent = useCallback(async () => {
    if (!current) return;
    const personalCount = dashboards.filter((d) => !d.is_system).length;
    if (personalCount >= MAX_DASHBOARDS) {
      setSaveError(`You can only have ${MAX_DASHBOARDS} dashboards. Delete one before creating another.`);
      return;
    }
    const existingNames = new Set(dashboards.map((d) => d.name));
    const baseName = `${current.name || 'Dashboard'} (copy)`;
    let candidate = baseName;
    let suffix = 2;
    while (existingNames.has(candidate)) {
      candidate = `${baseName} ${suffix}`;
      suffix += 1;
    }
    try {
      const created = await apiCreate({
        name: candidate,
        layout: current.layout || emptyLayout(),
        widgets: current.widgets || [],
      });
      const normalized = normalizeDashboardLayout(created);
      await refreshList();
      setCurrent(normalized);
      writeCachedDashboard(email, normalized);
      writeActiveDashboardId(email, normalized.id);
      lastSavedRef.current = { layout: normalized.layout, widgets: normalized.widgets || [] };
    } catch (err) {
      setSaveError(err?.message || 'Failed to duplicate dashboard');
    }
  }, [current, dashboards, refreshList, email]);

  const renameCurrent = useCallback(async (newName) => {
    if (!current) return;
    if (current.is_system || isVectorDashboardId(current.id)) return;
    try {
      const updated = await apiUpdate(current.id, { name: newName });
      const merged = { ...current, ...updated };
      setCurrent((prev) => (prev ? merged : prev));
      writeCachedDashboard(email, merged);
      await refreshList();
    } catch (err) {
      setSaveError(err?.message || 'Failed to rename dashboard');
    }
  }, [current, email, refreshList]);

  const makeCurrentDefault = useCallback(async () => {
    if (!current) return;
    // Setting Vector as default = clearing every personal is_default flag.
    // Vector itself isn't in the database; "default" is computed dynamically
    // from `none of the user's personal dashboards has is_default=true`.
    if (current.is_system || isVectorDashboardId(current.id)) {
      try {
        const list = await apiList();
        const stalePersonalDefaults = list.filter(
          (d) => d.is_default && !isVectorDashboardId(d.id),
        );
        if (stalePersonalDefaults.length > 0) {
          await Promise.all(
            stalePersonalDefaults.map((d) =>
              apiUpdate(d.id, { is_default: false }).catch((err) => {
                console.warn(`Failed to demote ${d.id}:`, err);
                return null;
              }),
            ),
          );
        }
        // Flip the toolbar pill on immediately; the picker dropdown is
        // reconciled by refreshList() below.
        setCurrent((prev) => (prev && (prev.is_system || isVectorDashboardId(prev.id))
          ? { ...prev, is_default: true }
          : prev));
        await refreshList();
      } catch (err) {
        setSaveError(err?.message || 'Failed to set default dashboard');
      }
      return;
    }
    try {
      const updated = await apiUpdate(current.id, { is_default: true });
      const merged = { ...current, ...updated };
      setCurrent((prev) => (prev ? merged : prev));
      writeCachedDashboard(email, merged);
      await refreshList();
    } catch (err) {
      setSaveError(err?.message || 'Failed to set default dashboard');
    }
  }, [current, email, refreshList]);

  const deleteCurrent = useCallback(async () => {
    if (!current) return;
    if (current.is_system || isVectorDashboardId(current.id)) return;
    try {
      const result = await apiDelete(current.id);
      clearCachedDashboard(email, current.id);
      const list = await apiList();
      writeCachedDashboardList(email, list);
      const personal = list.filter((d) => !isVectorDashboardId(d.id));
      const hasPersonalDefault = personal.some((d) => d.is_default);
      const vectorEntry = {
        id: VECTOR_DASHBOARD_ID,
        name: orgDashboardName(),
        is_default: !hasPersonalDefault,
        is_system: true,
      };
      setDashboards([vectorEntry, ...personal]);
      if (list.length === 0) {
        // Last personal dashboard deleted — fall back to Vector instead of an
        // empty page.
        const synthetic = normalizeDashboardLayout(buildVectorDashboard({ markDefault: true }));
        setCurrent(synthetic);
        writeActiveDashboardId(email, VECTOR_DASHBOARD_ID);
        lastSavedRef.current = null;
        return;
      }
      const targetId = result?.promotedDefaultId
        || (list.find((d) => d.is_default) || list[0]).id;
      const full = await apiGet(targetId);
      const normalized = normalizeDashboardLayout(full);
      setCurrent(normalized);
      writeCachedDashboard(email, normalized);
      writeActiveDashboardId(email, normalized.id);
      lastSavedRef.current = { layout: normalized.layout, widgets: normalized.widgets || [] };
    } catch (err) {
      setSaveError(err?.message || 'Failed to delete dashboard');
    }
  }, [current, email]);

  /* ── Layout mutations ──────────────────────────────────────────────────── */

  const updateLayout = useCallback((updater) => {
    setCurrent((prev) => {
      if (!prev) return prev;
      // System dashboards (Vector) are immutable unless the user is an
      // Admin/VectorAdmin — they edit the org-wide template, everyone else
      // sees a read-only view.
      const isVec = prev.is_system || isVectorDashboardId(prev.id);
      if (isVec && !canEditVectorRef.current) return prev;
      const nextLayout = updater(prev.layout || emptyLayout());
      const next = { ...prev, layout: nextLayout };
      layoutSeqRef.current += 1;
      writeCachedDashboard(email, next);
      scheduleSave(next);
      return next;
    });
  }, [email, scheduleSave]);

  const updateOrder = useCallback((updater) => {
    updateLayout((prev) => ({ ...prev, order: updater(prev.order || []) }));
  }, [updateLayout]);

  /**
   * Apply a placement patch (any subset of {col, row, w, h}) to a widget. Clamps to grid
   * bounds and refuses the change if it would overlap another visible widget.
   */
  const setWidgetPlacement = useCallback((id, patch) => {
    updateLayout((prev) => {
      const sizes = { ...(prev.sizes || {}) };
      const wgtType = (current?.widgets || []).find((w) => w.id === id)?.type || '';
      const cur = sizes[id] || {};
      const merged = clampPlacement({ ...cur, ...patch }, wgtType);
      const others = (prev.order || [])
        .filter((wid) => wid !== id)
        .map((wid) => sizes[wid])
        .filter(Boolean);
      if (placementCollides(merged, others)) return prev; // reject overlap
      sizes[id] = merged;
      return { ...prev, sizes };
    });
  }, [updateLayout, current]);

  const toggleVisibility = useCallback((id, hide) => {
    updateLayout((prev) => {
      const order = [...(prev.order || [])];
      const hidden = [...(prev.hidden || [])];
      const sizes = { ...(prev.sizes || {}) };
      if (hide) {
        const idx = order.indexOf(id);
        if (idx >= 0) order.splice(idx, 1);
        if (!hidden.includes(id)) hidden.push(id);
      } else {
        const idx = hidden.indexOf(id);
        if (idx >= 0) hidden.splice(idx, 1);
        if (!order.includes(id)) order.push(id);
        // Place the unhidden widget: keep its previous slot if it's still free, else find one.
        const wgtType = (current?.widgets || []).find((w) => w.id === id)?.type || '';
        const prevPlacement = sizes[id];
        const otherRects = order
          .filter((wid) => wid !== id)
          .map((wid) => sizes[wid])
          .filter(Boolean);
        if (!prevPlacement || placementCollides(prevPlacement, otherRects)) {
          const w = prevPlacement?.w || defaultWForType(wgtType);
          const h = prevPlacement?.h || defaultHForType(wgtType);
          sizes[id] = findFirstFreeSlot(w, h, otherRects);
        }
      }
      return { ...prev, order, hidden, sizes };
    });
  }, [updateLayout, current]);

  const addWidget = useCallback(({ type, config }) => {
    if (!current) return;
    const isVec = current.is_system || isVectorDashboardId(current.id);
    if (isVec && !canEditVectorRef.current) return;
    const meta = getWidgetMeta(type);
    if (!meta) return;
    const id = generateClientId();
    const newWidget = { id, type, config: config || {} };
    const layout = current.layout || emptyLayout();
    const placedRects = (layout.order || [])
      .map((wid) => layout.sizes?.[wid])
      .filter(Boolean);
    // Per-config default size overrides: weekWinRate KPI renders 5 weekday sub-cards,
    // so it needs a wider default than the standard single-metric KPI card.
    const defaultW = (type === 'kpi' && config?.metric === 'weekWinRate')
      ? Math.min(GRID_TRACKS, 12)
      : defaultWForType(type);
    const defaultH = (type === 'kpi' && config?.metric === 'weekWinRate')
      ? 2
      : defaultHForType(type);
    const slot = findFirstFreeSlot(defaultW, defaultH, placedRects);
    const next = {
      ...current,
      widgets: [...(current.widgets || []), newWidget],
      layout: {
        ...layout,
        order: [...(layout.order || []), id],
        sizes: { ...(layout.sizes || {}), [id]: slot },
        hidden: [...(layout.hidden || [])],
      },
    };
    layoutSeqRef.current += 1;
    setCurrent(next);
    writeCachedDashboard(email, next);
    setShowAddWidget(false);
    persistDashboard(next);
  }, [current, email, persistDashboard]);

  /**
   * Create a new Custom Multi-Strat combo from inside the widget customizer
   * (so users can mix strategies for a Calendar widget without leaving the
   * dashboard). Returns `custom:<id>` on success so the caller can select it,
   * or null on failure. Reuses the same API + app-wide combo list as the
   * Vector Results builder.
   */
  const createCombo = useCallback(async (payload) => {
    try {
      const resp = await fetch(API_ENDPOINTS.customMultistrats, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success || !data?.combo?.id) {
        throw new Error(data?.error || `Save failed (${resp.status})`);
      }
      // Refresh the app-wide combo list so the new combo appears everywhere.
      onCustomComboUpsert?.(data.combo);
      return `custom:${data.combo.id}`;
    } catch (err) {
      return { error: err?.message || 'Failed to save combo' };
    }
  }, [onCustomComboUpsert]);

  /**
   * Duplicate an existing widget. The clone gets a fresh id, the same type and config,
   * and is placed at the first row-major free slot using the source widget's size.
   */
  const duplicateWidget = useCallback((id) => {
    setCurrent((prev) => {
      if (!prev) return prev;
      const isVec = prev.is_system || isVectorDashboardId(prev.id);
      if (isVec && !canEditVectorRef.current) return prev;
      const src = (prev.widgets || []).find((w) => w.id === id);
      if (!src) return prev;
      const layout = prev.layout || emptyLayout();
      const placedRects = (layout.order || [])
        .map((wid) => layout.sizes?.[wid])
        .filter(Boolean);
      const srcPlacement = layout.sizes?.[id];
      const w = srcPlacement?.w || defaultWForType(src.type);
      const h = srcPlacement?.h || defaultHForType(src.type);
      const slot = findFirstFreeSlot(w, h, placedRects);
      const newId = generateClientId();
      const newWidget = { id: newId, type: src.type, config: { ...(src.config || {}) } };
      const next = {
        ...prev,
        widgets: [...(prev.widgets || []), newWidget],
        layout: {
          ...layout,
          order: [...(layout.order || []), newId],
          sizes: { ...(layout.sizes || {}), [newId]: slot },
          hidden: [...(layout.hidden || [])],
        },
      };
      layoutSeqRef.current += 1;
      writeCachedDashboard(email, next);
      persistDashboard(next);
      return next;
    });
  }, [email, persistDashboard]);

  /**
   * Patch a widget's `config` object — used by widgets that store their own state
   * (e.g. CurrentResultsTableWidget's hidden/order list of strategies).
   */
  const updateWidgetConfig = useCallback((id, patch) => {
    setCurrent((prev) => {
      if (!prev) return prev;
      const isVec = prev.is_system || isVectorDashboardId(prev.id);
      if (isVec && !canEditVectorRef.current) return prev;
      let changed = false;
      const widgets = (prev.widgets || []).map((w) => {
        if (w.id !== id) return w;
        changed = true;
        return { ...w, config: { ...(w.config || {}), ...(patch || {}) } };
      });
      if (!changed) return prev;
      const next = { ...prev, widgets };
      layoutSeqRef.current += 1;
      writeCachedDashboard(email, next);
      scheduleSave(next);
      return next;
    });
  }, [email, scheduleSave]);

  const removeWidgetPermanently = useCallback((id) => {
    setCurrent((prev) => {
      if (!prev) return prev;
      const isVec = prev.is_system || isVectorDashboardId(prev.id);
      if (isVec && !canEditVectorRef.current) return prev;
      const layout = prev.layout || emptyLayout();
      const sizes = { ...(layout.sizes || {}) };
      delete sizes[id];
      const next = {
        ...prev,
        widgets: (prev.widgets || []).filter((w) => w.id !== id),
        layout: {
          order: (layout.order || []).filter((wid) => wid !== id),
          hidden: (layout.hidden || []).filter((wid) => wid !== id),
          sizes,
        },
      };
      layoutSeqRef.current += 1;
      writeCachedDashboard(email, next);
      persistDashboard(next);
      return next;
    });
  }, [email, persistDashboard]);

  const resetLayout = useCallback(() => {
    if (!current) return;
    // Reset is a destructive personal-dashboard action; we don't expose it on
    // the Vector Dashboard regardless of tier (use Customize to edit instead).
    if (current.is_system || isVectorDashboardId(current.id)) return;
    const next = { ...current, layout: emptyLayout(), widgets: [] };
    layoutSeqRef.current += 1;
    setCurrent(next);
    writeCachedDashboard(email, next);
    persistDashboard(next);
    setConfirmReset(false);
    setCustomizing(true);
  }, [current, email, persistDashboard]);

  /* ── Drag-and-drop on the main grid ─────────────────────────────────────── */

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /**
   * Container ref + cell metrics ref. Drag-end and resize translate pixel deltas into
   * grid cells, so we need (cellWidth, rowHeight, gap) at the time of the event.
   */
  const gridRef = useRef(null);
  const cellMetricsRef = useRef({ cellW: 80, rowH: ROW_PX, gap: GRID_GAP_PX });

  const measureGrid = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;
    const styles = window.getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap) || GRID_GAP_PX;
    const innerWidth = el.clientWidth
      - parseFloat(styles.paddingLeft || '0')
      - parseFloat(styles.paddingRight || '0');
    const cellW = (innerWidth - gap * (GRID_TRACKS - 1)) / GRID_TRACKS;
    cellMetricsRef.current = { cellW: Math.max(1, cellW), rowH: ROW_PX, gap };
  }, []);

  useEffect(() => {
    measureGrid();
    const onResize = () => measureGrid();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureGrid]);

  const handleGridDragEnd = useCallback((event) => {
    const { active, delta } = event;
    if (!active) return;
    const dx = delta?.x || 0;
    const dy = delta?.y || 0;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    measureGrid();
    const metrics = cellMetricsRef.current;
    const dCol = Math.round(dx / Math.max(1, metrics.cellW + metrics.gap));
    const dRow = Math.round(dy / Math.max(1, metrics.rowH + metrics.gap));
    if (dCol === 0 && dRow === 0) return;
    // Read the current placement off the latest layout, then resolve the drop. On
    // collision we swap (if sizes match) or displace overlapping widgets to free slots
    // — never silently revert the drag.
    setCurrent((prev) => {
      if (!prev) return prev;
      const isVec = prev.is_system || isVectorDashboardId(prev.id);
      if (isVec && !canEditVectorRef.current) return prev;
      const sizes = prev.layout?.sizes || {};
      const cur = sizes[active.id];
      if (!cur) return prev;
      const wgtType = (prev.widgets || []).find((w) => w.id === active.id)?.type || '';
      const target = clampPlacement({ ...cur, col: cur.col + dCol, row: cur.row + dRow }, wgtType);
      const newSizes = resolveDrop(sizes, active.id, cur, target);
      const next = {
        ...prev,
        layout: { ...prev.layout, sizes: newSizes },
      };
      layoutSeqRef.current += 1;
      writeCachedDashboard(email, next);
      scheduleSave(next);
      return next;
    });
  }, [measureGrid, email, scheduleSave]);

  /* ── Render ─────────────────────────────────────────────────────────────── */

  const widgetsById = useMemo(() => {
    const m = new Map();
    (current?.widgets || []).forEach((w) => m.set(w.id, w));
    return m;
  }, [current?.widgets]);

  if (loading) {
    return (
      <div className="feature-gate-stack">
        {!suppressPageHeading ? <PageHeading title="Overview" description="Your at-a-glance overview of strategy performance." /> : null}
        <div className="dashboard-page-loading" role="status" aria-live="polite">Loading dashboard…</div>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="feature-gate-stack">
        {!suppressPageHeading ? <PageHeading title="Overview" description="Your at-a-glance overview of strategy performance." /> : null}
        <div className="upgrade-required-page">
          <div className="upgrade-required-content">
            <div className="pro-lock-icon">
              <IconLock size={48} />
            </div>
            <h2 className="pro-lock-title">Dashboard</h2>
            <p className="pro-lock-subtitle">Sign in to continue</p>
            <p className="pro-lock-description">
              Sign in to view your dashboard, pin widgets, and track strategy performance at a glance.
            </p>
            <button
              type="button"
              className="pro-lock-upgrade-button"
              onClick={() => onNavigate && onNavigate('login')}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <>
        {!suppressPageHeading ? <PageHeading title="Overview" description="Your at-a-glance overview of strategy performance." /> : null}
        <div className="dashboard-page-error" role="alert">{pageError}</div>
      </>
    );
  }

  const layout = current?.layout || emptyLayout();
  const order = layout.order || [];
  const isCurrentSystem = !!(current?.is_system || isVectorDashboardId(current?.id));
  // The system dashboard (Vector) is normally non-customizable — but Admin /
  // VectorAdmin edit it directly here, so they retain customize mode on it.
  const effectiveCustomizing = customizing && (!isCurrentSystem || canEditVector);
  const useVcLayout = dashboardHasVcWidgets(current);
  const useStackedVcLayout = useVcLayout && !effectiveCustomizing;

  const dashboardBody = (
    <>
      {!suppressPageHeading ? (
        <div className="dashboard-titlebar">
          <PageHeading
            title="Overview"
            description={`${getEntityDisplayName()} strategy dashboard — customize or duplicate to make it yours.`}
            className="dashboard-titlebar-heading"
          />
        </div>
      ) : null}
      {/* Brand Admins: the steps that take a brand from empty to live (hides once done). */}
      {(isVectorAdmin || isAdmin) && !suppressPageHeading ? <BrandGettingStarted onNavigate={onNavigate} /> : null}
      <DashboardToolbar
        dashboards={dashboards}
        current={current}
        customizing={effectiveCustomizing}
        canCustomize={canCustomize}
        canEditVector={canEditVector}
        onSwitchDashboard={switchDashboard}
        onCreate={createDashboard}
        onDuplicate={duplicateCurrent}
        onRename={renameCurrent}
        onMakeDefault={makeCurrentDefault}
        onDelete={() => setConfirmDelete(true)}
        onResetLayout={() => setConfirmReset(true)}
        onToggleCustomize={() => setCustomizing((v) => !v)}
        onCreateOwnDashboard={() => {
          // From the Vector dashboard, the CTA routes Basic users to the
          // upgrade modal; Pro/Max/Admin/VectorAdmin go straight to the
          // create-new-dashboard flow.
          if (!canCustomize) {
            if (typeof onUpgrade === 'function') {
              onUpgrade('Upgrade to RiskLo Pro to create and customize your own dashboards.');
            }
            return;
          }
          createDashboard();
        }}
        onUpgrade={onUpgrade}
        saving={saving}
        saveError={saveError}
      />

      {canCustomize && customizing && current
        && (!isCurrentSystem || canEditVector) ? (
          <CustomizePanel
            dashboard={current}
            customCombos={customCombos}
            onReorder={updateOrder}
            onToggleVisibility={toggleVisibility}
            onAddWidget={() => setShowAddWidget(true)}
            onRemove={removeWidgetPermanently}
            onClose={() => setCustomizing(false)}
          />
      ) : null}


      {current && !isCurrentSystem && canCustomize && hasTradeWatcher && order.length > 0
        && !twDismissed.includes(current.id)
        && !(current.widgets || []).some((w) => isTradeWatcherWidgetType(w.type)) ? (
          <div className="tw-dash-prompt" role="status">
            <span>
              <strong>Your Trade Watcher accounts aren’t on this dashboard.</strong>
              Add today’s NinjaTrader P&amp;L, daily P&amp;L and alerts at the top.
            </span>
            <span className="tw-dash-prompt-actions">
              <button type="button" className="is-primary" onClick={addTradeWatcherSection}>Add Trade Watcher</button>
              <button type="button" onClick={() => dismissTwPrompt(current.id)}>Not now</button>
            </span>
          </div>
        ) : null}
      {!current ? (
        <div className="dashboard-empty-state">
          <div className="dashboard-empty-state-inner">
            <div className="dashboard-empty-state-icon"><IconChart size={48} /></div>
            {canCustomize && personalOnly ? (
              <>
                <h3>Build your dashboard</h3>
                <p>Start from a layout made for your accounts, then make it yours: add, move and resize widgets.</p>
                <TemplateGallery onPick={createDashboardFrom} />
              </>
            ) : canCustomize ? (
              <>
                <h3>Create your first dashboard</h3>
                <p>Build a personalized view of any {getEntityDisplayName()} strategy with KPI cards, charts, and tables.</p>
                <button type="button" className="dashboard-toolbar-btn dashboard-toolbar-btn-primary" onClick={createDashboard}>
                  <IconPlus size={14} /> New dashboard
                </button>
              </>
            ) : (
              <>
                <h3>Dashboard unavailable</h3>
                <p>The default dashboard couldn’t be loaded. Please try again later.</p>
              </>
            )}
          </div>
        </div>
      ) : order.length === 0 ? (
        <div className="dashboard-empty-state">
          <div className="dashboard-empty-state-inner">
            <div className="dashboard-empty-state-icon"><IconChart size={48} /></div>
            {canCustomize ? (
              <>
                <h3>This dashboard is empty</h3>
                <p>Add a widget to start building your view.</p>
                <button type="button" className="dashboard-toolbar-btn dashboard-toolbar-btn-primary" onClick={() => setShowAddWidget(true)}>
                  <IconPlus size={14} /> Add your first widget
                </button>
              </>
            ) : (
              <>
                <h3>Dashboard is empty</h3>
                <p>An admin hasn’t set up the default dashboard yet.</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGridDragEnd}>
          <div
            ref={gridRef}
            className={[
              'dashboard-grid',
              effectiveCustomizing ? 'is-customizing' : '',
              useStackedVcLayout ? 'is-stacked-vc' : '',
            ].filter(Boolean).join(' ')}
          >
            {order.map((id) => {
              const widget = widgetsById.get(id);
              if (!widget) return null;
              const meta = getWidgetMeta(widget.type);
              const placement = getWidgetPlacement(layout, id, widget.type);
              const minW = minWForType(widget.type);
              const minH = minHForType(widget.type);

              const handlePlacementChange = (patch) => setWidgetPlacement(id, patch);

              if (!meta) {
                return (
                  <GridWidget
                    key={id}
                    id={id}
                    customizing={effectiveCustomizing}
                    placement={placement}
                    minW={minW}
                    minH={minH}
                    widgetType={widget.type}
                    widgetConfig={widget.config}
                    cellMetricsRef={cellMetricsRef}
                    onPlacementChange={handlePlacementChange}
                    onHide={() => toggleVisibility(id, true)}
                    onDuplicate={() => duplicateWidget(id)}
                  >
                    <div className="dashboard-widget dashboard-widget-crash">
                      <div className="dashboard-widget-header">
                        <span className="dashboard-widget-title">Unknown widget</span>
                      </div>
                      <div className="dashboard-widget-body">
                        <div className="dashboard-widget-state">Type "{widget.type}" is not registered.</div>
                      </div>
                    </div>
                  </GridWidget>
                );
              }

              const Component = meta.component;
              const fieldCount = (meta.configFields ? meta.configFields(() => []).length : 0);
              return (
                <GridWidget
                  key={id}
                  id={id}
                  customizing={effectiveCustomizing}
                  placement={placement}
                  minW={minW}
                  minH={minH}
                  widgetType={widget.type}
                  widgetConfig={widget.config}
                  cellMetricsRef={cellMetricsRef}
                  onPlacementChange={handlePlacementChange}
                  onHide={() => toggleVisibility(id, true)}
                  onDuplicate={() => duplicateWidget(id)}
                  onEdit={fieldCount > 0 ? () => setEditWidgetId(id) : undefined}
                  onConfigure={fieldCount > 0 && canCustomize && (!isCurrentSystem || canEditVector)
                    ? () => setEditWidgetId(id)
                    : undefined}
                >
                  <WidgetErrorBoundary>
                    {isVcWidgetType(widget.type) ? (
                      <div className="vector-capital-widget-embed">
                        <Component config={widget.config || {}} />
                      </div>
                    ) : (
                      <Component
                        config={widget.config || {}}
                        user={user}
                        onNavigate={onNavigate}
                        customCombos={customCombos}
                        editing={effectiveCustomizing}
                        onConfigChange={(patch) => updateWidgetConfig(id, patch)}
                      />
                    )}
                  </WidgetErrorBoundary>
                </GridWidget>
              );
            })}
          </div>
        </DndContext>
      )}

      <TemplatePickerModal
        open={showTemplatePicker}
        onPick={createDashboardFrom}
        onClose={() => setShowTemplatePicker(false)}
      />
      <AddWidgetModal
        isOpen={showAddWidget || !!editWidgetId}
        existingWidget={editWidgetId ? (current?.widgets || []).find((w) => w.id === editWidgetId) || null : null}
        onClose={() => {
          setShowAddWidget(false);
          setEditWidgetId(null);
        }}
        onSave={({ type, config }) => {
          if (editWidgetId) {
            updateWidgetConfig(editWidgetId, config);
            setEditWidgetId(null);
          } else {
            addWidget({ type, config });
          }
        }}
        sheetNames={sheetNames}
        customCombos={customCombos}
        onCreateCombo={createCombo}
        comboLimitReached={(customCombos || []).length >= MAX_CUSTOM_COMBOS}
        loadingSheets={loadingSheets}
        widgetTypeGroups={widgetTypeGroupsForDashboard(current, { personalOnly, companyDashboard: isCurrentSystem })}
        companyDashboard={isCurrentSystem}
      />

      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete dashboard?"
        message={`Delete "${current?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          deleteCurrent();
        }}
      />

      <ConfirmModal
        isOpen={confirmReset}
        title="Reset dashboard layout?"
        message="This removes every widget on this dashboard. You can add widgets again afterwards."
        confirmLabel="Reset"
        danger
        onCancel={() => setConfirmReset(false)}
        onConfirm={resetLayout}
      />
    </>
  );

  return (
    <VectorCapitalDashboardHost
      user={user}
      isAdmin={Boolean(isAdmin || isVectorAdmin)}
      isFullAdmin={Boolean(isAdmin)}
      enabled={useVcLayout}
    >
      {dashboardBody}
    </VectorCapitalDashboardHost>
  );
}

/**
 * Tier-gating wrapper. Signed-in Basic users see the Vector dashboard preview
 * behind a Pro lock overlay; Pro+ can view and customize personal dashboards.
 */
function DashboardPage({
  user,
  isPro,
  isVectorAdmin,
  isAdmin,
  tierReady,
  sheetNames,
  customCombos = [],
  onCustomComboUpsert,
  loadingSheets,
  hasVectorAccess = null,
  membershipDeniedText = '',
  onNavigate,
  onUpgrade,
}) {
  const signedIn = Boolean(user?.email && String(user.email).trim());
  // Self users (no company): a welcome Overview with their own accounts and dashboards.
  const workspace = useWorkspace(signedIn);
  // How many personal dashboards a self user has (cached list first, so the
  // full welcome doesn't flash for someone who already built one).
  const [personalCount, setPersonalCount] = useState(() => (
    signedIn ? (readCachedDashboardList(user.email) || []).filter((d) => !isVectorDashboardId(d.id)).length : 0
  ));

  // While tier resolves, render nothing — avoids a flash of the wrong UI
  // (e.g. read-only for someone who actually has Max).
  if (!tierReady) return null;

  if (signedIn && workspace.loading) return null;
  if (signedIn && workspace.selfUser) {
    return (
      <WelcomeOverview
        user={user}
        isPro={Boolean(isPro)}
        onNavigate={onNavigate}
        onUpgrade={onUpgrade}
        hasDashboards={Boolean(isPro) && personalCount > 0}
        dashboards={(
          <DashboardPageInner
            user={user}
            sheetNames={[]}
            customCombos={customCombos}
            onCustomComboUpsert={onCustomComboUpsert}
            loadingSheets={false}
            onNavigate={onNavigate}
            canCustomize
            canEditVector={false}
            isAdmin={false}
            isVectorAdmin={false}
            onUpgrade={onUpgrade}
            suppressPageHeading
            personalOnly
            onPersonalCountChange={setPersonalCount}
          />
        )}
      />
    );
  }

  if (signedIn && (loadingSheets || hasVectorAccess === null)) {
    return (
      <div className="feature-gate-stack">
        <PageHeading
          title="Overview"
          description="Your at-a-glance overview of strategy performance."
        />
        <div
          className="results-tier-placeholder"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="results-tier-placeholder-inner">
            <span className="results-tier-placeholder-spin" aria-hidden="true">
              <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
            </span>
            <p className="results-tier-placeholder-text">Loading your account access…</p>
          </div>
        </div>
      </div>
    );
  }

  if (signedIn && hasVectorAccess === false) {
    return (
      <div className="feature-gate-stack">
        <PageHeading
          title="Overview"
          description="Your at-a-glance overview of strategy performance."
        />
        <div className="results-spreadsheet-error" role="alert">
          {membershipDeniedText || membershipDeniedFallbackMessage()}
        </div>
      </div>
    );
  }

  // Customizing / creating dashboards is a paid feature (Pro / Max / Admin /
  // VectorAdmin — AuthContext rolls Max/Admin into `isPro`). Basic users can now
  // VIEW the org-wide Vector Dashboard read-only (no full-page Pro lock); the
  // customize + create controls are gated by `canCustomize` inside the page.
  const canCustomize = Boolean(isPro);
  // Editing the org-wide Vector Dashboard is gated more tightly. Only Admin
  // and VectorAdmin can publish changes that propagate to every signed-in user.
  const canEditVector = Boolean(isAdmin || isVectorAdmin);

  return (
    <DashboardPageInner
      user={user}
      sheetNames={sheetNames}
      customCombos={customCombos}
      onCustomComboUpsert={onCustomComboUpsert}
      loadingSheets={loadingSheets}
      onNavigate={onNavigate}
      canCustomize={canCustomize}
      canEditVector={canEditVector}
      isAdmin={isAdmin}
      isVectorAdmin={isVectorAdmin}
      onUpgrade={onUpgrade}
    />
  );
}

export default DashboardPage;
